// operator-signals.ts — the operator casino's signals (SC-61815), relayed by the SDK's casino bridge from the window that
// embeds the game (SC-62154: no list of casino origins; not framed, there is no bridge). A reality check, the cashier,
// a responsible-gaming interruption or maintenance must STOP the game: REAL rounds may not keep settling behind the
// operator's overlay. These are presentation holds, never authorization: whoever embeds the game can also resume it —
// a restriction that must hold is the wallet refusing the debit (round-outcome.ts: player_locked).
//   - game.pause / ui.blocked: a running autobet ends (an interruption is not a pause of the run: the player re-arms it
//     on purpose), the game pauses through its own setPaused (Space and the CTA bail on S.paused), and held() makes the
//     round driver refuse every round. ui.blocked also gets the SDK's own blocked overlay (overlay-style.ts shows it).
//   - game.resume: releases both holds — the SDK's overlay reducer clears a blocked overlay on resume too, so the game
//     and the screen agree — and un-pauses ONLY when the game may run (mayResume: driver installed, no fatal, no
//     reboot). A resume that arrives before the boot finished must never un-freeze an un-driven game.
//     With NOTHING held it does nothing at all: the casino answers every sdk.ready with its whole state, so a resume
//     arrives at every load of a game it does not hold, and again whenever it says its state — and the game's own
//     setPaused(false) is not free (it repaints, wiping a message the rail just showed, and re-arms a running
//     autobet's timer).
import type { GameSdk } from "@blocklabsdev/rgs-game-manager-frontend-api";

export interface PausableGame {
  readonly S: { auto: unknown };
  stopAuto(why?: string): void;
  setPaused(paused: boolean): void;
}

export interface OperatorHold {
  /** True while the operator has the game paused or blocked: no round may be sent. */
  held(): boolean;
}

export function holdOnOperatorSignals(
  sdk: Pick<GameSdk, "on">,
  game: PausableGame,
  opts: { readonly mayResume: () => boolean },
): OperatorHold {
  let paused = false;
  let blocked = false;
  const hold = (): void => {
    if (game.S.auto) game.stopAuto("Paused by the casino");
    game.setPaused(true);
  };
  sdk.on("game.pause", () => {
    paused = true;
    hold();
  });
  sdk.on("ui.blocked", () => {
    blocked = true; // the operator's reason is never shown: the SDK overlay presents its own copy
    hold();
  });
  sdk.on("game.resume", () => {
    if (!paused && !blocked) return; // nothing was held: nothing to release
    paused = false;
    blocked = false;
    if (opts.mayResume()) game.setPaused(false);
  });
  return { held: () => paused || blocked };
}
